import React, { useState, useEffect, useCallback, FormEvent, useRef } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from '../api/client';
import { formatPhone, formatPhoneInput } from '../utils/formatPhone';
import { parseServerDate } from '../utils/dateUtils';
import type {
  FinancialAccountListResponse,
  FinancialAccountResponse,
  CreateFinancialAccountRequest,
  WitnessAccessVoteResponse,
} from '../types/api';
import { VaultRole, FinancialAccountType } from '../types/api';
import AlertMessage from './AlertMessage';
import Skeleton from './Skeleton';
import EmptyState from './EmptyState';
import SortableList from './SortableList';
import { useToast } from './ToastContext';
import './FinancialAccountsList.css';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

const ACCOUNT_TYPE_LABELS: Record<FinancialAccountType, string> = {
  [FinancialAccountType.Checking]:      'Checking',
  [FinancialAccountType.Savings]:       'Savings',
  [FinancialAccountType.Brokerage]:     'Brokerage',
  [FinancialAccountType.IRA]:           'IRA',
  [FinancialAccountType.RothIRA]:       'Roth IRA',
  [FinancialAccountType.FourOhOneK]:    '401(k)',
  [FinancialAccountType.Pension]:       'Pension',
  [FinancialAccountType.LifeInsurance]: 'Life Insurance',
  [FinancialAccountType.CreditCard]:    'Credit Card',
  [FinancialAccountType.Mortgage]:      'Mortgage',
  [FinancialAccountType.Other]:         'Other',
  [FinancialAccountType.OnlineAccount]:  'Online Account',
};

const BLANK_FORM: CreateFinancialAccountRequest = {
  institutionName: '',
  accountType: FinancialAccountType.Checking,
  accountNumber: '',
  contactPhone: '',
  website: '',
  notes: '',
};

export default function FinancialAccountsList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const { addToast } = useToast();

  // ── Account list state ──
  const [accounts, setAccounts]   = useState<FinancialAccountResponse[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showForm, setShowForm]   = useState(false);

  // ── Add form ──
  const [form, setForm]           = useState<CreateFinancialAccountRequest>(BLANK_FORM);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');

  // ── Edit form ──
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [editForm, setEditForm]     = useState<CreateFinancialAccountRequest>(BLANK_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  // ── Delete ──
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ── Account number reveal (form toggles) ──
  const [showAddAccountNum, setShowAddAccountNum]   = useState(false);
  const [showEditAccountNum, setShowEditAccountNum] = useState(false);
  const [revealedAccountNums, setRevealedAccountNums] = useState<Set<string>>(new Set());

  // ── Witness password lock state ──
  const [witnessPasswordAccess, setWitnessPasswordAccess] = useState(false);
  const [activeVote, setActiveVote]                       = useState<WitnessAccessVoteResponse | null>(null);
  const [showUnlockConfirm, setShowUnlockConfirm]         = useState(false);
  const [showRequestConfirm, setShowRequestConfirm]       = useState(false);
  const [toggling, setToggling]                           = useState(false);
  const [voteLoading, setVoteLoading]                     = useState(false);

  // ── Derived ──
  const canEdit       = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;
  const isWitness     = userRole === VaultRole.Witness;
  const isWitnessLocked = isWitness && !witnessPasswordAccess;
  const canVote       = userRole !== VaultRole.Advisor;

  function toggleRevealAccountNum(id: string) {
    setRevealedAccountNums(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Data loading
  // ─────────────────────────────────────────────────────────────────────────

  const loadAccounts = useCallback(async () => {
    try {
      const data = await apiGet<FinancialAccountListResponse>(
        `/api/vault/${vaultId}/financial-accounts`
      );
      setAccounts(data.accounts);
      setWitnessPasswordAccess(data.witnessPasswordAccess);
      setActiveVote(data.activeAccessVote);
      onCountChangeRef.current?.(data.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load financial accounts.');
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  async function handleReorder(reordered: FinancialAccountResponse[]) {
    setAccounts(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/financial-accounts/reorder`, {
        orderedIds: reordered.map(a => a.id),
      });
    } catch {
      await loadAccounts();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Add / Edit / Delete handlers
  // ─────────────────────────────────────────────────────────────────────────

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!form.institutionName.trim()) { setFormError('Institution name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      await apiPost(`/api/vault/${vaultId}/financial-accounts`, {
        ...form,
        accountNumber: form.accountNumber || null,
        contactPhone: form.contactPhone || null,
        website: form.website || null,
        notes: form.notes || null,
      });
      setForm(BLANK_FORM);
      setShowForm(false);
      await loadAccounts();
    } catch (err: any) {
      setFormError(err.message || 'Failed to add account.');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(account: FinancialAccountResponse) {
    setShowEditAccountNum(false);
    setEditingId(account.id);
    setEditForm({
      institutionName: account.institutionName,
      accountType: account.accountType,
      accountNumber: account.accountNumber ?? '',
      contactPhone: account.contactPhone ?? '',
      website: account.website ?? '',
      notes: account.notes ?? '',
    });
    setEditError('');
  }

  async function handleEdit(e: FormEvent) {
    e.preventDefault();
    if (!editForm.institutionName.trim()) { setEditError('Institution name is required.'); return; }
    setEditSaving(true);
    setEditError('');
    try {
      await apiPut(`/api/vault/${vaultId}/financial-accounts/${editingId}`, {
        ...editForm,
        accountNumber: editForm.accountNumber || null,
        contactPhone: editForm.contactPhone || null,
        website: editForm.website || null,
        notes: editForm.notes || null,
      });
      setEditingId(null);
      await loadAccounts();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update account.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete(accountId: string) {
    setDeletingId(accountId);
    try {
      await apiDelete(`/api/vault/${vaultId}/financial-accounts/${accountId}`);
      await loadAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete account.');
    } finally {
      setDeletingId(null);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Witness lock / vote handlers
  // ─────────────────────────────────────────────────────────────────────────

  async function handleToggleLock(newValue: boolean) {
    // Unlocking requires confirmation; locking is immediate
    if (newValue) {
      setShowUnlockConfirm(true);
      return;
    }
    setToggling(true);
    try {
      await apiPatch(`/api/vault/${vaultId}/witness-access/toggle`, { value: false });
      setWitnessPasswordAccess(false);
      setActiveVote(null);
      addToast('success', 'Account numbers are now locked for Witnesses.');
    } catch (err: any) {
      addToast('error', err.message || 'Failed to update witness access.');
    } finally {
      setToggling(false);
    }
  }

  async function handleConfirmUnlock() {
    setShowUnlockConfirm(false);
    setToggling(true);
    try {
      await apiPatch(`/api/vault/${vaultId}/witness-access/toggle`, { value: true });
      setWitnessPasswordAccess(true);
      setActiveVote(null);
      addToast('success', 'Witnesses can now see account numbers.');
      await loadAccounts(); // Reload so accounts show eye icons
    } catch (err: any) {
      addToast('error', err.message || 'Failed to update witness access.');
    } finally {
      setToggling(false);
    }
  }

  function handleLockIconClick() {
    if (activeVote?.hasActiveRequest) {
      addToast('info', 'An access request is already pending — see the vote panel above.');
      return;
    }
    setShowRequestConfirm(true);
  }

  async function handleRequestVote() {
    setShowRequestConfirm(false);
    setVoteLoading(true);
    try {
      const result = await apiPost<WitnessAccessVoteResponse>(
        `/api/vault/${vaultId}/witness-access/vote/request`
      );
      setActiveVote(result);
      addToast('success', 'Access request sent. Your name is visible to all vault members.');
    } catch (err: any) {
      addToast('error', err.message || 'Failed to send access request.');
    } finally {
      setVoteLoading(false);
    }
  }

  async function handleCastVote(vote: boolean) {
    setVoteLoading(true);
    try {
      const result = await apiPost<WitnessAccessVoteResponse>(
        `/api/vault/${vaultId}/witness-access/vote/cast`,
        { vote }
      );
      setActiveVote(result);
      if (!result.hasActiveRequest) {
        // Majority reached — reload to reflect new open access
        await loadAccounts();
        addToast('success', 'Access unlocked by majority vote.');
      } else {
        addToast('success', vote ? 'You voted Yes.' : 'You voted No.');
      }
    } catch (err: any) {
      addToast('error', err.message || 'Failed to cast vote.');
    } finally {
      setVoteLoading(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) return <Skeleton variant="row" count={3} />;

  return (
    <div>
      {/* ── Page header ── */}
      <div className="financial-header">
        <h3>Important Accounts</h3>
        {canEdit && (
          <button className="btn-text" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Add Account'}
          </button>
        )}
      </div>

      <p className="financial-description">
        Bank accounts, investments, insurance policies, online services, and more — everything
        your family needs to locate and access your important accounts.
      </p>

      <AlertMessage type="error" message={error} />

      {/* ── Witness access toggle banner (Owner / Spouse only) ── */}
      {canEdit && (
        <div className={`financial-witness-banner ${witnessPasswordAccess ? 'financial-witness-banner--open' : 'financial-witness-banner--locked'}`}>
          <div className="financial-witness-banner-info">
            <span className="financial-witness-banner-icon" aria-hidden="true">
              {witnessPasswordAccess ? '🔓' : '🔒'}
            </span>
            <div>
              <span className="financial-witness-banner-label">
                Witness access: {witnessPasswordAccess ? 'account numbers visible' : 'account numbers locked'}
              </span>
              <span className="financial-witness-banner-sub">
                {witnessPasswordAccess
                  ? 'Witnesses can currently see account numbers.'
                  : 'Witnesses see a lock icon instead of account numbers.'}
              </span>
            </div>
          </div>
          <button
            className={`btn-text ${witnessPasswordAccess ? 'btn-text--danger' : ''}`}
            onClick={() => handleToggleLock(!witnessPasswordAccess)}
            disabled={toggling}
            aria-label={witnessPasswordAccess ? 'Lock account numbers from Witnesses' : 'Unlock account numbers for Witnesses'}
          >
            {toggling ? '…' : witnessPasswordAccess ? 'Lock' : 'Unlock'}
          </button>
        </div>
      )}

      {/* ── Active vote panel (visible to all eligible roles when a request is open) ── */}
      {activeVote?.hasActiveRequest && (
        <div className="financial-vote-panel">
          <div className="financial-vote-panel-header">
            <span className="financial-vote-panel-icon" aria-hidden="true">🗳️</span>
            <span className="financial-vote-panel-title">Access Request</span>
          </div>
          <p className="financial-vote-panel-requester">
            <strong>{activeVote.requestedByName}</strong> has requested access to account numbers.
          </p>
          {activeVote.requestedAt && (
            <p className="financial-vote-panel-date">
              Requested {parseServerDate(activeVote.requestedAt)?.toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric'
              })}
            </p>
          )}

          {activeVote.votes.length > 0 && (
            <div className="financial-vote-chips">
              {activeVote.votes.map(v => (
                <span
                  key={v.vaultMemberId}
                  className={`financial-vote-chip ${v.vote ? 'financial-vote-chip--yes' : 'financial-vote-chip--no'}`}
                >
                  {v.vote ? '✓' : '✗'} {v.memberName ?? 'Member'}
                </span>
              ))}
            </div>
          )}

          <p className="financial-vote-tally">
            {activeVote.yesCount} Yes · {activeVote.noCount} No · {activeVote.eligibleVoterCount} eligible voters
          </p>

          {canVote && (
            <div className="financial-vote-actions">
              <button
                className="financial-vote-btn financial-vote-btn--yes"
                onClick={() => handleCastVote(true)}
                disabled={voteLoading}
              >
                👍 Vote Yes
              </button>
              <button
                className="financial-vote-btn financial-vote-btn--no"
                onClick={() => handleCastVote(false)}
                disabled={voteLoading}
              >
                👎 Vote No
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Add form ── */}
      {showForm && canEdit && (
        <form className="financial-form" onSubmit={handleAdd} autoComplete="off">
          <div className="financial-form-row">
            <div className="form-group">
              <label className="form-label">Institution Name *</label>
              <input
                className="form-input"
                value={form.institutionName}
                onChange={e => setForm(f => ({ ...f, institutionName: e.target.value }))}
                placeholder="e.g. Fidelity Investments"
                maxLength={200}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Account Type</label>
              <select
                className="form-input"
                value={form.accountType}
                onChange={e => setForm(f => ({ ...f, accountType: Number(e.target.value) as FinancialAccountType }))}
              >
                {Object.entries(ACCOUNT_TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="financial-form-row">
            <div className="form-group">
              <label className="form-label">Account Number</label>
              <div className="financial-secret-wrap">
                <input
                  className="form-input"
                  type={showAddAccountNum ? 'text' : 'password'}
                  value={form.accountNumber ?? ''}
                  onChange={e => setForm(f => ({ ...f, accountNumber: e.target.value.slice(0, 50) }))}
                  placeholder="Account number"
                  maxLength={50}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="financial-eye-btn"
                  onClick={() => setShowAddAccountNum(v => !v)}
                  aria-label={showAddAccountNum ? 'Hide account number' : 'Show account number'}
                >
                  {showAddAccountNum ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Contact Phone</label>
              <input
                className="form-input"
                value={form.contactPhone ?? ''}
                onChange={e => setForm(f => ({ ...f, contactPhone: formatPhoneInput(e.target.value) }))}
                placeholder="(800) 555-0100"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Website</label>
            <input
              className="form-input"
              value={form.website ?? ''}
              onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
              placeholder="https://www.fidelity.com"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input form-textarea"
              value={form.notes ?? ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Account details, login hints, advisor name, etc."
              rows={3}
            />
          </div>
          {formError && <p className="financial-form-error">{formError}</p>}
          <div className="financial-form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Add Account'}
            </button>
            <button type="button" className="btn-text" onClick={() => { setShowForm(false); setFormError(''); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* ── Empty state ── */}
      {accounts.length === 0 && !showForm && (
        <EmptyState
          icon="documents"
          title="No important accounts yet"
          subtitle={canEdit
            ? 'Add bank accounts, investments, online services, and more so your family can locate them.'
            : 'No accounts have been added to this vault.'}
        />
      )}

      {/* ── Account cards ── */}
      <div className="financial-list">
        <SortableList
          items={accounts}
          canReorder={canEdit}
          onReorder={handleReorder}
          renderItem={(account) => (
          <div className="financial-card">
            {editingId === account.id ? (
              // ── Edit form ──
              <form className="financial-form" onSubmit={handleEdit} autoComplete="off">
                <div className="financial-form-row">
                  <div className="form-group">
                    <label className="form-label">Institution Name *</label>
                    <input
                      className="form-input"
                      value={editForm.institutionName}
                      onChange={e => setEditForm(f => ({ ...f, institutionName: e.target.value }))}
                      maxLength={200}
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Account Type</label>
                    <select
                      className="form-input"
                      value={editForm.accountType}
                      onChange={e => setEditForm(f => ({ ...f, accountType: Number(e.target.value) as FinancialAccountType }))}
                    >
                      {Object.entries(ACCOUNT_TYPE_LABELS).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="financial-form-row">
                  <div className="form-group">
                    <label className="form-label">Account Number</label>
                    <div className="financial-secret-wrap">
                      <input
                        className="form-input"
                        type={showEditAccountNum ? 'text' : 'password'}
                        value={editForm.accountNumber ?? ''}
                        onChange={e => setEditForm(f => ({ ...f, accountNumber: e.target.value.slice(0, 50) }))}
                        maxLength={50}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="financial-eye-btn"
                        onClick={() => setShowEditAccountNum(v => !v)}
                        aria-label={showEditAccountNum ? 'Hide account number' : 'Show account number'}
                      >
                        {showEditAccountNum ? '🙈' : '👁'}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Phone</label>
                    <input
                      className="form-input"
                      value={editForm.contactPhone ?? ''}
                      onChange={e => setEditForm(f => ({ ...f, contactPhone: formatPhoneInput(e.target.value) }))}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input
                    className="form-input"
                    value={editForm.website ?? ''}
                    onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-input form-textarea"
                    value={editForm.notes ?? ''}
                    onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
                {editError && <p className="financial-form-error">{editError}</p>}
                <div className="financial-form-actions">
                  <button type="submit" className="btn-primary" disabled={editSaving}>
                    {editSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button type="button" className="btn-text" onClick={() => setEditingId(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              // ── Read view ──
              <>
                <div
                  className="financial-card-header"
                  onClick={() => setExpandedId(expandedId === account.id ? null : account.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && setExpandedId(expandedId === account.id ? null : account.id)}
                >
                  <div className="financial-card-title">
                    <span className="financial-institution">{account.institutionName}</span>
                    {account.hasAccountNumber && (
                      isWitnessLocked ? (
                        <button
                          type="button"
                          className="financial-lock-btn"
                          onClick={e => { e.stopPropagation(); handleLockIconClick(); }}
                          title="Account number locked — tap to request access"
                          aria-label="Account number locked"
                        >
                          🔒
                        </button>
                      ) : (
                        <span className="financial-last-four">••••••••</span>
                      )
                    )}
                  </div>
                  <div className="financial-card-meta">
                    <span className="financial-type-badge">{ACCOUNT_TYPE_LABELS[account.accountType]}</span>
                    <span className="financial-chevron" aria-hidden="true">
                      {expandedId === account.id ? '▲' : '▼'}
                    </span>
                  </div>
                </div>

                {expandedId === account.id && (
                  <div className="financial-card-body">
                    {/* Account number — locked view vs. normal reveal */}
                    {account.hasAccountNumber && isWitnessLocked ? (
                      <div className="financial-field">
                        <span className="financial-field-label">Acct #</span>
                        <span className="financial-lock-display">
                          <span aria-hidden="true">🔒</span>
                          <span className="financial-lock-label">Locked — request access above</span>
                        </span>
                      </div>
                    ) : account.accountNumber ? (
                      <div className="financial-field">
                        <span className="financial-field-label">Acct #</span>
                        <span className="financial-secret-display">
                          <span>{revealedAccountNums.has(account.id) ? account.accountNumber : '••••••••'}</span>
                          <button
                            type="button"
                            className="financial-eye-btn financial-eye-btn--inline"
                            onClick={() => toggleRevealAccountNum(account.id)}
                            aria-label={revealedAccountNums.has(account.id) ? 'Hide account number' : 'Show account number'}
                          >
                            {revealedAccountNums.has(account.id) ? '🙈' : '👁'}
                          </button>
                        </span>
                      </div>
                    ) : null}

                    {account.contactPhone && (
                      <div className="financial-field">
                        <span className="financial-field-label">Phone</span>
                        <span>{formatPhone(account.contactPhone)}</span>
                      </div>
                    )}
                    {account.website && (
                      <div className="financial-field">
                        <span className="financial-field-label">Website</span>
                        <a href={account.website} target="_blank" rel="noopener noreferrer" className="financial-link">
                          {account.website}
                        </a>
                      </div>
                    )}
                    {account.notes && (
                      <div className="financial-field financial-field--notes">
                        <span className="financial-field-label">Notes</span>
                        <span className="financial-notes-text">{account.notes}</span>
                      </div>
                    )}
                    {canEdit && (
                      <div className="financial-card-actions">
                        <button className="btn-text" onClick={() => startEdit(account)}>Edit</button>
                        <button
                          className="btn-text btn-text--danger"
                          onClick={() => handleDelete(account.id)}
                          disabled={deletingId === account.id}
                        >
                          {deletingId === account.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          )}
        />
      </div>

      {/* ── Confirm unlock modal (Owner/Spouse) ── */}
      {showUnlockConfirm && (
        <div className="financial-confirm-overlay" onClick={() => setShowUnlockConfirm(false)}>
          <div className="financial-confirm-box" onClick={e => e.stopPropagation()}>
            <h3 className="financial-confirm-title">Unlock Account Numbers?</h3>
            <p className="financial-confirm-body">
              Witnesses will be able to see all account numbers in this vault.
              You can re-lock them at any time.
            </p>
            <div className="financial-confirm-actions">
              <button className="btn-text" onClick={() => setShowUnlockConfirm(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleConfirmUnlock} disabled={toggling}>
                {toggling ? 'Unlocking…' : 'Unlock Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm request access modal (Witness) ── */}
      {showRequestConfirm && (
        <div className="financial-confirm-overlay" onClick={() => setShowRequestConfirm(false)}>
          <div className="financial-confirm-box" onClick={e => e.stopPropagation()}>
            <h3 className="financial-confirm-title">Request Access to Account Numbers?</h3>
            <p className="financial-confirm-body">
              Your name will be shown to all vault members as the requester.
              A majority vote is required before account numbers are revealed.
            </p>
            <div className="financial-confirm-actions">
              <button className="btn-text" onClick={() => setShowRequestConfirm(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleRequestVote} disabled={voteLoading}>
                {voteLoading ? 'Requesting…' : 'Request Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
